import React from "react";
import Header from "../../components/Header";
import { Box, Stack } from "@mui/material";
import LoadingComponent from "../../components/LoadingComponent";
import ErrorComponent from "../../components/ErrorComponent";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import InfoButton from "../../components/InfoButton";
import { useGetApplicationsReportQuery, useGetProductsReportQuery } from "../../services/reportService";
import { getApplicationStatusByValue, getProductUOMByName, getTicketTypeByValue } from "../../constants/enums";

const Export = () => {
  const today = dayjs();
  const { data: currentWeekApplications, currentWeekApplicationsError, currentWeekApplicationsIsLoading } = useGetApplicationsReportQuery({
    offset: 0,
    limit: 1000,
    state: 1,
    start_date: today.startOf("week").format("YYYY-MM-DD"),
    end_date: today.endOf("week").format("YYYY-MM-DD")
  }, {
    refetchOnMountOrArgChange: true,
  });

  const { data: nextWeekApplications, nextWeekApplicationsError, nextWeekApplicationsIsLoading } = useGetApplicationsReportQuery({
    offset: 0,
    limit: 1000,
    state: 1,
    start_date: today.add(1, "week").startOf("week").format("YYYY-MM-DD"),
    end_date: today.add(1, "week").endOf("week").format("YYYY-MM-DD")
  }, {
    refetchOnMountOrArgChange: true,
  });

  
  const { data: currentWeekProducts, currentWeekProductsError, currentWeekProductsIsLoading } = useGetProductsReportQuery({
    offset: 0,
    limit: 1000,
    state: 1,
    start_date: today.startOf("week").format("YYYY-MM-DD"),
    end_date: today.endOf("week").format("YYYY-MM-DD")
  }, {
    refetchOnMountOrArgChange: true,
  });

  const { data: nextWeekProducts, nextWeekProductsError, nextWeekProductsIsLoading } = useGetProductsReportQuery({
    offset: 0,
    limit: 1000,
    state: 1,
    start_date: today.add(1, "week").startOf("week").format("YYYY-MM-DD"),
    end_date: today.add(1, "week").endOf("week").format("YYYY-MM-DD")
  }, {
    refetchOnMountOrArgChange: true,
  });

  
  // Loading and error handling
  if ( currentWeekApplicationsIsLoading || nextWeekApplicationsIsLoading || currentWeekProductsIsLoading || nextWeekProductsIsLoading ) {
    return <LoadingComponent />;
  }

  if ( currentWeekApplicationsError || nextWeekApplicationsError || currentWeekProductsError || nextWeekProductsError ) {
    return <ErrorComponent />;
  }

  const handleExportProducts = async (products, fileName) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Productos");
  
    // Add header row
    const headerRow = worksheet.addRow([
      "PRODUCTO",
      "CANTIDAD"
    ]);
  
    // Style header row
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" }, // Light gray background
      };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    });

    
    worksheet.getColumn(1).width = 22; // PRODUCTO
    worksheet.getColumn(2).width = 15; // CANTIDAD
  
    (products?.records || []).forEach((record) => {
      const row = worksheet.addRow([
        record.product_name,
        record.total_dosage_quantity
      ]);

      row.eachCell((cell) => {
        cell.alignment = { vertical: "middle", wrapText: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFF" }, // Light gray background
      };
      });
    });
  
    // Write the workbook to a file and trigger download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
  }

  // Function to handle export for current week applications
  const handleExportApplications = async (applications, fileName) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Aplicaciones");
  
    // Add header row
    const headerRow = worksheet.addRow([
      "APLICACIÓN",
      "SEMANA",
      "ESTADO",
      "LOTE",
      "FECHA PROGRAMADA",
      "TIPO",
      "LITROS X H",
      "PRODUCTO",
      "DOSIS X H",
      "UDM",
      "BOOMS 2000L",
      "PRODUCTO TOTAL",
    ]);
  
    // Style header row
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" }, // Light gray background
      };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    });
  
    // Set column widths
    worksheet.getColumn(1).width = 20; // APLICACIÓN
    worksheet.getColumn(2).width = 10; // SEMANA
    worksheet.getColumn(3).width = 15; // ESTADO
    worksheet.getColumn(4).width = 20; // LOTE
    worksheet.getColumn(5).width = 20; // FECHA PROGRAMADA
    worksheet.getColumn(6).width = 15; // TIPO
    worksheet.getColumn(7).width = 15; // LITROS X H
    worksheet.getColumn(8).width = 22; // PRODUCTO
    worksheet.getColumn(9).width = 15; // DOSIS X H
    worksheet.getColumn(10).width = 18; // PRODUCTO UOM
    worksheet.getColumn(11).width = 10; // BOOMS
    worksheet.getColumn(12).width = 15; // PRODUCTO TOTAL
  
    (applications?.records || []).forEach((record) => {
      const startRow = worksheet.rowCount;
  
      // Get the color for the ticket type
      const ticketType = getTicketTypeByValue(record.application.ticket_type);
      const rowColor = ticketType.softColor.replace("#", ""); // Remove '#' from hex color
  
      // Add rows for the application (product details)
      for (let i = 0; i < record.dosages.length; i++) {
        const dosage = record.dosages[i];
        const row = worksheet.addRow([
          record.application.name, // APLICACIÓN
          dayjs(record.application.programmed_date).week(), // SEMANA
          getApplicationStatusByValue(record.application.state).label, // ESTADO
          record.application.lote_name + " " + record.application.location, // LOTE
          dayjs(record.application.programmed_date).format("DD-MM-YYYY"), // FECHA PROGRAMADA
          ticketType.label, // TIPO
          2000, // LITROS X H
          dosage.product_name, // PRODUCTO
          dosage.dosage_quantity, // DOSAGE X H
          getProductUOMByName(dosage.product_uom).label, // PRODUCTO UOM
          record.application.total_area, // BOONES
          dosage.total_quantity || 0, // PRODUCTO TOTAL
        ]);
  
        // Apply row color
        row.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: rowColor }, // Use the color from ticket type
          };
          cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        });
      }
  
      const endRow = worksheet.rowCount - 1;
  
      // Merge cells for application-level details
      if (startRow < endRow) {
        for (let col = 1; col <= 7; col++) {
          worksheet.mergeCells(startRow + 1, col, endRow + 1, col);
        }
      }
    });
  
    // Write the workbook to a file and trigger download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
  };

    return (
      <Box m="20px">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Header title="Export" subtitle="EXPORT BUTTON" />
        </Box>
        <Stack direction="row" spacing={2} mt={2}>
          <InfoButton label="Aplicaciones Semana Actual" onClick={() => {handleExportApplications(currentWeekApplications, "Aplicaciones Semana Actual.xlsx")}} />
          <InfoButton label="Aplicaciones Semana Siguiente" onClick={() => {handleExportApplications(nextWeekApplications, "Aplicaciones Semana Siguiente.xlsx")}} />
        </Stack>
        <Stack direction="row" spacing={2} mt={2}>
          <InfoButton label="Productos Semana Actual" onClick={() => {handleExportProducts(currentWeekProducts, "Productos Semana Actual")}} />
          <InfoButton label="Productos Semana Siguiente" onClick={() => {handleExportProducts(nextWeekProducts, "Productos Semana Siguiente")}} />
        </Stack>
      </Box>
    );
  };

export default Export;